import { Button, Drawer, Link, Typography } from "@shared/ui.primitives";
import { CloseIcon } from "components/SVG";
import Image from "next/image";
import { clsx } from "clsx";
import { useSegment } from "tracking";
import type {
  MultipleProductCard,
  MultipleProductCardVariant
} from "@shared/graphql.types";
import * as styles from "./ShadeSelectorDrawer.css";
import {
  createShadeButtonClickedTrackingObject,
  createCloseShadeSelectorTrackingObject,
  createProductClickedTrackingObject
} from "../utils/trackingUtils";
import { useEffect, useRef } from "react";

interface ShadeSelectorDrawerProps {
  product: MultipleProductCard;
  activeVariant: MultipleProductCardVariant;
  isOpen: boolean;
  onClose: () => void;
  onATB: () => void;
  onShadeSelect: (index: number) => void;
  resultId?: string;
  // styleVariant?: "default" | "scrollShadow";
  location: string;
}

export const ShadeSelectorDrawer = ({
  product,
  activeVariant,
  isOpen,
  onClose,
  onShadeSelect,
  resultId,
  location,
  onATB
  // styleVariant = "default"
}: ShadeSelectorDrawerProps) => {
  const { track } = useSegment();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  const handleShadeClick = (index: number) => {
    onShadeSelect(index);

    const trackingObject = createShadeButtonClickedTrackingObject(
      product,
      activeVariant,
      location
    );

    track({
      event: "Page Element Clicked",
      properties: trackingObject
    });
  };

  const handleClose = () => {
    const locationTypeDetails = `${product.title} ${product.brandName} ${
      activeVariant?.shade ? activeVariant?.shade : ""
    }`;

    const trackingObject = createCloseShadeSelectorTrackingObject(
      locationTypeDetails,
      location
    );

    track({
      event: "Page Element Clicked",
      properties: trackingObject
    });

    onClose();
  };

  const handleAddToBag = () => {
    const trackingObject = createProductClickedTrackingObject(
      product,
      activeVariant,
      {
        location_type: "PLP Shade Selector",
        location_type_details: "Add to Basket Button",
        result_id: resultId
      }
    );

    track({
      event: "Product Clicked",
      properties: trackingObject
    });
    onATB();
    onClose();
  };

  const handleViewDetails = () => {
    const trackingObject = createProductClickedTrackingObject(
      product,
      activeVariant,
      {
        location_type: "PLP Shade Selector",
        location_type_details: "View More Details Link",
        result_id: resultId
      }
    );

    track({
      event: "Product Clicked",
      properties: trackingObject
    });
  };

  return (
    <Drawer
      isOpen={isOpen}
      handleDismiss={handleClose}
      ariaDescription="See more shade options for this product"
      ariaLabel="Shade Selector"
      id="shade-selector-drawer"
      dataTestId="shade-selector-drawer"
      headerId="shade-selector-drawer-header"
    >
      <Drawer.Header
        id="shade-selector-drawer-header"
        handleDismiss={handleClose}
      >
        Shade Options
      </Drawer.Header>
      <Drawer.Body>
        <ul className={styles.list}>
          {product?.variants?.map((shade, index) => {
            if (!shade) return null;
            const isOutOfStock = !shade?.availability?.inStock;
            const isActiveShade = shade.sku === activeVariant.sku;

            return (
              <li key={shade.sku}>
                <button
                  type="button"
                  tabIndex={0}
                  onClick={() => handleShadeClick(index)}
                  onKeyDown={() => handleShadeClick(index)}
                  className={clsx(
                    styles.link,
                    isActiveShade && styles.activeLink,
                    isOutOfStock && styles.oosLink
                  )}
                >
                  <div className={styles.linkContent}>
                    {shade.swatch && (
                      <Image
                        src={shade?.swatch.src}
                        alt={shade?.shade ?? shade.swatch.description}
                        height={45}
                        width={45}
                      />
                    )}
                    {isOutOfStock && (
                      <CloseIcon
                        width="45px"
                        className={styles.outOfStockIcon}
                      />
                    )}
                    <Typography as="p" variant="body-md">
                      {shade.shade}
                    </Typography>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </Drawer.Body>
      <Drawer.Footer>
        <div className={styles.footerContent}>
          <Button
            width="full"
            variant="primary"
            onClick={handleAddToBag}
            text="Add to Basket"
            disabled={!activeVariant?.availability?.inStock}
          />
          <Link
            href={activeVariant?.href || "#"}
            text="View More Details"
            width="full"
            variant="primary"
            textAlign="center"
            onClick={handleViewDetails}
          />
        </div>
      </Drawer.Footer>
    </Drawer>
  );
};
